import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown, Loader2, ShieldCheck, Tag } from "lucide-react";
import { useT } from "@/lib/i18n";
import { formatPrice } from "@/utils/priceUtils";
import tracking from "@/lib/tracking";
import { useGuaranteeDays, useNativeStripeEnabled } from "@/lib/features";
import { useIsNative } from "@/lib/platform";
import ShrinkingBeat from "../ShrinkingBeat";
import { BEAT_TRANSITION } from "../beats";
import {
  BLEED,
  HEADING_TOP,
  LIST_GAP,
  ROW_BORDER,
  ROW_SHAPE,
  ROW_SURFACE,
  ROW_SURFACE_SELECTED,
} from "../layout";
import { useGetPaymentOptions } from "@/lib/api/usePayment";
import { useProfile, useCreateReservation } from "@/lib/api";
import { useToast } from "@/components/ux/Toast";
import { Loader } from "@/components/Loader";
import { PaymentModal } from "@/components/booking/PaymentModal";
import { BookingSuccess } from "../../booking/BookingSuccess";
import type { UserAPI } from "@shared/types";
import { weeklyEquivalent } from "../plans";
import type { PlansScreenDef, ScreenComponentProps } from "../types";

/**
 * The offer.
 *
 * Three deliberate choices, each answering something the numbers say:
 *
 * 1. The headline argument is that one outing is a coin flip and the product
 *    works over a few. That is the honest case for a membership over a single
 *    seat, and specifically for the multi-month plan — the current mix is
 *    3,194 one-month against 425 three-month, so plan *length* is the lever,
 *    not membership-vs-one-time. It also pre-empts the "my one dinner was
 *    fine, I guess" churn behind 2,278 cancellations.
 *
 * 2. The guarantee sits ABOVE the cards, not in the fine print. It is doing
 *    more work than any discount could: it removes the risk that stops
 *    someone committing to three months of a thing they have not tried.
 *
 * 3. There is a way out, and it is a text link rather than a button. A
 *    prominent escape guts the offer; no escape at all reads as a trap and
 *    invites a back-button exit we never see again. This one is measured, and
 *    it returns the user here rather than dumping them into the app.
 *
 * Pressing a plan goes straight to paying for it — there is no Continue on
 * this screen. A separate confirm step after choosing a plan is a second
 * decision about a decision already made, and it is the last place in the
 * funnel where an extra press costs anything.
 */

/**
 * The screen plays itself, in the flow's own beat language: the headline lands
 * alone at full size, shrinks back the way every handoff's first beat does,
 * and the case for a membership opens under it a line at a time.
 *
 * The order is the argument. Doing it regularly is what works (headline), and
 * then the one piece of evidence for it that comes from members rather than
 * from us. Prices after that, and the guarantee last — under the prices,
 * where it answers the objection they raise rather than pre-empting one
 * nobody has had yet.
 *
 * Showing all of it at once makes it a page of marketing to skim; paced, it
 * reads as being talked through.
 */

/**
 * The proof lines, in order, under `${copyKey}.proofs`. Each is a figure and a
 * sentence.
 *
 * One, deliberately. A rating and a volume figure sat here too, and three
 * numbers in a column read as a landing page rather than as an argument —
 * and both of them were about the company, where this one is about what
 * members do next. Adding another means adding it here and to the copy.
 */
const PROOFS = ["repeat"] as const;

/**
 * Milliseconds each stage holds before the next opens. The first is the long
 * one: it is the only stretch with nothing else on screen to read. One entry
 * per transition — headline → each proof → prices → guarantee.
 */
const STAGE_HOLD_MS = [2100, 950, 950];
/** Stage at which the prices are out. Everything priced keys off this. */
const PRICES_STAGE = 1 + PROOFS.length;
const CARD_STAGGER = 0.09;

/**
 * How long to wait for a purchase to show up on the profile before leaving.
 *
 * A card payment finishes on Stripe's side, not ours: the subscription is
 * created by a webhook, which lands a second or two later. Leaving the moment
 * the card clears sends someone to the booking flow whose entitlement has not
 * arrived, and the reservation there asks them to pay for the seat they have
 * just bought a membership for.
 *
 * Bounded, and it leaves either way. A webhook that is slow (or, on a dev
 * machine, never forwarded) is not a reason to trap someone on the offer.
 */
const ENTITLEMENT_POLL_MS = 1200;
const ENTITLEMENT_POLLS = 8;

/** What the shrunken headline dims to. Still the argument, so barely at all. */
const HEADLINE_SHRUNK_OPACITY = 0.72;

/** Opening a section: the beat curve, over a shorter distance. */
const OPEN_TRANSITION = { duration: 0.6, ease: BEAT_TRANSITION.ease };

/**
 * A part of the story opening under the one before it.
 *
 * Grows its box rather than fading in at full height — the same reason the
 * handoff beats do (see StatementScreen): a fade leaves the layout jumping by
 * the full height on the first frame, and what should read as "opens under it"
 * reads as "appears, then everything shuffles".
 */
function StoryBeat({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <motion.div
      // Anything already open when the screen mounts is simply *there*. Framer
      // reads `initial` once, so deriving it from `open` is what stops the
      // opening beat growing itself out of nothing on the first frame —
      // the kicker unfolding and shoving the headline down as the screen
      // arrives, which is a second entrance competing with the screen's own.
      // It also means the reduced-motion path, where every beat starts open,
      // animates nothing at all.
      initial={open ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
      animate={open ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
      transition={OPEN_TRANSITION}
      style={{ overflow: "hidden" }}
    >
      {children}
    </motion.div>
  );
}

export default function PlansScreen({
  def,
  rt,
}: ScreenComponentProps<PlansScreenDef>) {
  const { t } = useT();
  const guaranteeDays = useGuaranteeDays();
  const reducedMotion = useReducedMotion();
  // Which parts of the story are out. Anyone who has asked not to be animated
  // at gets the whole screen at once rather than a slideshow they did not ask
  // for — the pacing is the presentation, never a gate on the content.
  const [stage, setStage] = useState(reducedMotion ? STAGE_HOLD_MS.length : 0);
  // Declared here, ahead of the story, because the story waits on it: see the
  // entitlement check below.
  const [gate, setGate] = useState<"checking" | "offer" | "member">("checking");
  useEffect(() => {
    // The pacing starts when the offer does. Otherwise a slow profile lookup
    // spends the opening beats behind a loader and the story arrives halfway
    // through itself.
    if (gate !== "offer") return;
    if (stage >= STAGE_HOLD_MS.length) return;
    const id = setTimeout(() => setStage((s) => s + 1), STAGE_HOLD_MS[stage]);
    return () => clearTimeout(id);
  }, [stage, gate]);
  // The same options the booking flow and the paywall price from, rather than
  // a table of our own. They come back in the user's currency, with any
  // referral or coupon already applied, which no amount of hardcoding could
  // reproduce — the previous placeholders were USD for everyone.
  const getPaymentOptions = useGetPaymentOptions();
  const [options, setOptions] = useState<UserAPI.PaymentOption[] | null>(null);
  const [payingFor, setPayingFor] = useState<UserAPI.PaymentOption | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const load = getPaymentOptions.mutateAsync;
  useEffect(() => {
    let cancelled = false;
    load({})
      .then((r) => {
        if (cancelled || !r.success || !r.data) return;
        setOptions(r.data.paymentOptions);
        setStartDate(r.data.startDate ?? null);
      })
      .catch(() => {
        // The offer is not the last thing standing between them and an
        // account, so a pricing failure must not block the flow. The escape
        // link still leaves; they just see no cards.
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  /**
   * A coupon, entered here rather than in the checkout sheet.
   *
   * Pressing a plan skips the sheet's plan list (and its coupon field) and
   * goes straight to the card form, so this is the only place in onboarding a
   * code can be typed. Applying one re-prices the cards from the same
   * endpoint, and the discounted options are what the checkout is handed, so
   * the intent carries the coupon. In-app purchases cannot take a coupon, so
   * the field is not offered there at all.
   */
  const isNative = useIsNative();
  const nativeStripe = useNativeStripeEnabled();
  const couponsSupported = !isNative || nativeStripe;
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const {
    data: profileData,
    isLoading: profileLoading,
    refetch: refetchProfile,
  } = useProfile();
  const createReservation = useCreateReservation();
  const toast = useToast();
  /**
   * They already pay for this.
   *
   * A member reaching the offer is being sold something they have: the seat
   * they just picked is already covered, and the only honest thing to put in
   * front of them is the booking. `type === "subscription"` specifically —
   * an unused single-seat entitlement covers this night, but it is not a
   * standing membership, and the offer is still worth making to someone
   * holding one.
   */
  const isMember = (profileData?.data?.entitlements ?? []).some(
    (e) => e.type === "subscription" && e.status === "active",
  );
  /** The seat, once it exists. Everything after this point pays for it. */
  const [booking, setBooking] = useState<UserAPI.Reservation | null>(null);
  const [reserving, setReserving] = useState(false);
  /** Paid, waiting for the membership to exist server-side. */
  const [confirming, setConfirming] = useState(false);
  /** Paid and seated: the same congratulations any other booking ends on. */
  const [celebrating, setCelebrating] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const onPaid = useCallback(async () => {
    setPayingFor(null);
    setConfirming(true);
    for (let i = 0; i < ENTITLEMENT_POLLS; i++) {
      const r = await refetchProfile();
      const live = (r.data?.data?.entitlements ?? []).some(
        (e) => e.status === "active",
      );
      if (live || !mounted.current) break;
      await new Promise((resolve) => setTimeout(resolve, ENTITLEMENT_POLL_MS));
    }
    if (!mounted.current) return;
    // A seat was booked, so this ends where every other booking ends: the
    // congratulations screen, with its confetti and its "what happens next".
    // Without one — they skipped the night and only bought a membership —
    // there is nothing to congratulate, so the flow just finishes.
    if (booking) setCelebrating(true);
    else rt.finish();
  }, [refetchProfile, rt, booking]);

  const selectedEventId = rt.answers.selectedEventId as string | undefined;
  const selectedNeighborhoodId = rt.answers.selectedNeighborhoodId as
    | string
    | undefined;
  /** Everything the reservation needs was answered on the events screen. */
  const canReserve = Boolean(selectedEventId && selectedNeighborhoodId);

  /**
   * Whether there is an offer to make at all, decided before any of it is
   * drawn.
   *
   * `checking` while the profile is still in flight, so a member never sees a
   * price flash past on the way to their confirmation. It is normally a single
   * frame: the profile is fetched when the flow mounts and is warm by the time
   * anyone reaches the last screen.
   */
  const rtRef = useRef(rt);
  rtRef.current = rt;
  const reserveRef = useRef(createReservation.mutateAsync);
  reserveRef.current = createReservation.mutateAsync;

  /**
   * A member's seat, booked on arrival.
   *
   * The endpoint already knew this — it answers `requiresPayment: false` for
   * anyone entitled, and `choosePlan` has always honoured that. What it could
   * not do is stop the offer being made in the first place: the story played,
   * the prices arrived, and a paying member had to pick a plan they already
   * had before anything would book. So the same reservation is made here
   * instead, without being asked for, and the screen goes straight to the
   * congratulations.
   *
   * If the reservation comes back wanting payment after all — a membership
   * that lapsed between the profile being fetched and this running — the offer
   * is what's left, with the seat already held.
   */
  useEffect(() => {
    if (gate !== "checking" || profileLoading) return;
    if (!isMember) {
      setGate("offer");
      return;
    }
    setGate("member");
    // Nothing to book and nothing to sell them. The flow is over.
    if (!canReserve) {
      rtRef.current.finish();
      return;
    }
    setReserving(true);
    void (async () => {
      try {
        const response = await reserveRef.current({
          eventTimeId: selectedEventId!,
          neighborhoodId: selectedNeighborhoodId!,
        });
        if (!mounted.current) return;
        if (!response.success || !response.data) {
          toast.error(response.error || t(`${def.copyKey}.reserveFailed`));
          setGate("offer");
          return;
        }
        setBooking(response.data.booking);
        rtRef.current.setAnswer("reservedBookingId", response.data.booking.id);
        if (response.data.requiresPayment === false) {
          setCelebrating(true);
          return;
        }
        if (response.data.paymentOptions?.length) {
          setOptions(response.data.paymentOptions);
        }
        if (response.data.startDate) setStartDate(response.data.startDate);
        setGate("offer");
      } catch (e: any) {
        if (!mounted.current) return;
        toast.error(
          e?.response?.data?.error || t(`${def.copyKey}.reserveFailed`),
        );
        setGate("offer");
      } finally {
        if (mounted.current) setReserving(false);
      }
    })();
    // Runs once, when the profile has answered. `rt` and the mutation are read
    // through refs because both change identity on every render of the flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate, profileLoading, isMember, canReserve]);

  // The flow's order, not the endpoint's — see `plans` in flow.ts for why the
  // single seat goes last. Anything the endpoint adds that the flow doesn't
  // know about still shows, after the ones it does.
  const ordered = options
    ? [...options].sort((a, b) => {
        const rank = (id: string) => {
          const i = def.plans.indexOf(id);
          return i < 0 ? def.plans.length : i;
        };
        return rank(a.id) - rank(b.id);
      })
    : [];

  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      toast.error(t("payment.coupon.enterCode"));
      return;
    }
    setApplyingCoupon(true);
    try {
      const r = await load({ couponCode: code, bookingId: booking?.id });
      if (!r.success || !r.data) {
        toast.error(r.error || t("payment.coupon.invalid"));
        return;
      }
      setOptions(r.data.paymentOptions);
      setAppliedCoupon(r.data.couponCode || code);
      toast.success(t("payment.coupon.applied"));
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t("payment.coupon.invalid"));
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = async () => {
    setAppliedCoupon(null);
    setCouponCode("");
    try {
      const r = await load({ bookingId: booking?.id });
      if (r.success && r.data) setOptions(r.data.paymentOptions);
    } catch {
      // The cards keep the discounted prices until the next load; the
      // checkout re-validates the coupon either way.
    }
    toast.success(t("payment.coupon.removed"));
  };

  /**
   * A press is the decision, and it books the seat.
   *
   * The seat is reserved *first*, then paid for — the same order the booking
   * page uses, and the reason a one-time payment works here at all: it is a
   * charge against a reservation, so without one the single seat had to be
   * handed off to another page to be bought, and the purchase and the night
   * came apart. Onboarding now has everything the reservation needs (the
   * night and the neighbourhood, both answered on the events screen), so it
   * makes it here and hands the checkout a real `bookingId`.
   *
   * A reservation that already exists is reused: pressing a second plan after
   * backing out of the sheet must not book a second seat.
   */
  const choosePlan = async (plan: UserAPI.PaymentOption) => {
    rt.setAnswer("selectedPlan", plan.id);

    // Nothing to reserve — they skipped the night. Memberships can still be
    // bought on their own; the seat gets claimed later, from the app.
    if (!canReserve) {
      setPayingFor(plan);
      return;
    }
    if (booking) {
      setPayingFor(plan);
      return;
    }

    setReserving(true);
    try {
      const response = await createReservation.mutateAsync({
        eventTimeId: selectedEventId!,
        neighborhoodId: selectedNeighborhoodId!,
      });
      if (!response.success || !response.data) {
        toast.error(response.error || t(`${def.copyKey}.reserveFailed`));
        return;
      }
      setBooking(response.data.booking);
      // Read by the host page when the flow ends: a seat already booked means
      // the booking page has nothing left to ask, so they go to the dashboard
      // and find it waiting.
      rt.setAnswer("reservedBookingId", response.data.booking.id);
      // Already entitled — a membership from before, or a plan bought and not
      // yet used. The seat is theirs; there is nothing to charge for.
      if (response.data.requiresPayment === false) {
        void onPaid();
        return;
      }
      // The reservation prices its options without the coupon, so with one
      // applied they are re-priced for this booking instead. Taking the
      // reservation's would quietly charge full price.
      const repriced = appliedCoupon
        ? await load({
            couponCode: appliedCoupon,
            bookingId: response.data.booking.id,
          }).catch(() => null)
        : null;
      if (repriced?.success && repriced.data) {
        setOptions(repriced.data.paymentOptions);
      } else if (!appliedCoupon && response.data.paymentOptions?.length) {
        setOptions(response.data.paymentOptions);
      }
      if (response.data.startDate) setStartDate(response.data.startDate);
      setPayingFor(plan);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.error || t(`${def.copyKey}.reserveFailed`),
      );
    } finally {
      setReserving(false);
    }
  };

  if (celebrating && booking) {
    return (
      // Full-bleed: the success card brings its own padding and background,
      // and the flow's gutter around it made it read as a card inside a card.
      <div className={`flex-1 flex flex-col ${BLEED}`}>
        <BookingSuccess
          bookingData={{
            eventTimeId: booking.eventTimeId,
            neighborhoodId: booking.neighborhoodId,
          }}
          paymentAmount={0}
          paymentCurrency={payingFor?.currency ?? "usd"}
          onViewDashboard={() => rt.finish()}
          // Same meaning as everywhere else this screen appears: back to the
          // booking page to pick another night.
          onBookAnother={() => rt.finish("booking")}
        />
      </div>
    );
  }

  // Nothing yet: it is not known whether this person is being sold anything.
  if (gate === "checking") {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (confirming || reserving) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <Loader className="mb-5" />
        <p className="text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400 max-w-[280px]">
          {t(`${def.copyKey}.${confirming ? "confirming" : "reserving"}`)}
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      <div className={HEADING_TOP}>
        {/* The kicker belongs to the opening beat alone: it introduces the
            headline, and once the headline has been read and stepped back it
            is introducing something the reader is already past. */}
        <StoryBeat open={stage === 0}>
          <p className="font-display font-medium text-[16px] leading-snug text-neutral-500 dark:text-neutral-400 mb-2">
            {t(`${def.copyKey}.kicker`)}
          </p>
        </StoryBeat>

        {/* Full size and alone first, then back to make room — the same move
            the handoff beats make, so the offer arrives in the flow's voice
            rather than as a page that was already scrolled to. */}
        <ShrinkingBeat
          shrunk={stage > 0}
          shrunkOpacity={HEADLINE_SHRUNK_OPACITY}
        >
          <h2 className="font-display font-semibold text-[34px] leading-[1.16] tracking-tight text-neutral-900 dark:text-neutral-50">
            {t(`${def.copyKey}.title`)}
          </h2>
        </ShrinkingBeat>

        {/* The recurrence argument itself, which is why it stays: everything
            below is an answer to it. No entrance of its own — it belongs to
            the opening beat, and the handoffs' first beat doesn't fade in
            either (see StatementScreen's `initial={i === 0 ? false : …}`).
            The screen transition is the entrance. */}
        <p className="text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400 mt-3">
          {t(`${def.copyKey}.subtitle`)}
        </p>
      </div>

      {/* The evidence for the headline, one line at a time. Each is a figure
          and a plain sentence — a statistic dressed up as a badge reads as
          marketing, and three badges in a row read as a landing page. */}
      {PROOFS.map((proof, i) => (
        <StoryBeat key={proof} open={stage >= i + 1}>
          <p className="pt-4 text-[13px] leading-relaxed text-neutral-600 dark:text-neutral-300">
            <span className="font-Poppins font-semibold text-neutral-900 dark:text-neutral-50">
              {t(`${def.copyKey}.proofs.${proof}.stat`)}
            </span>{" "}
            {t(`${def.copyKey}.proofs.${proof}.body`)}
          </p>
        </StoryBeat>
      ))}

      {/* And only now the prices, as the answer to all of it. */}
      <div className={`mt-5 flex flex-col ${LIST_GAP}`}>
        {(stage >= PRICES_STAGE ? ordered : []).map((plan, i) => {
          const isRecommended = plan.id === def.recommended;
          const weekly = weeklyEquivalent(plan);
          return (
            <motion.button
              key={plan.id}
              type="button"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                // One card at a time, on the story's last stage.
                delay: CARD_STAGGER * i,
                type: "spring",
                stiffness: 320,
                damping: 26,
              }}
              whileTap={{ scale: 0.985 }}
              onClick={() => choosePlan(plan)}
              className={`relative w-full text-left flex items-center gap-3 ${ROW_BORDER} ${ROW_SHAPE} ${
                isRecommended ? ROW_SURFACE_SELECTED : ROW_SURFACE
              }`}
            >
              {isRecommended && (
                <motion.span
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: CARD_STAGGER * i + 0.22 }}
                  className="absolute -top-2 left-4 rounded-full bg-secondary-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white"
                >
                  {t(`${def.copyKey}.recommendedBadge`)}
                </motion.span>
              )}
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-[15px] text-neutral-900 dark:text-neutral-50">
                  {plan.name}
                </span>
                <span className="block text-[12px] leading-snug text-neutral-500 dark:text-neutral-400 mt-1">
                  {plan.description}
                </span>
              </span>
              {/* Memberships are priced per week, which is what makes the
                  longer ones read as the better deal. The single seat has no
                  week to divide by, so it showed nothing at all: the one
                  option somebody lands on *because* they want to know what one
                  night costs was the only one without a number on it. It gets
                  its own price, labelled for what it buys. */}
              <span className="flex-none text-right">
                {/* With a coupon on, the price it replaced, struck through:
                    a discount nobody can see is not doing its job. */}
                {plan.original_amount != null &&
                  plan.original_amount > plan.price && (
                    <span className="block text-[11px] text-neutral-500 dark:text-neutral-400 line-through">
                      {formatPrice(
                        weeklyEquivalent({ ...plan, price: plan.original_amount }) ??
                          plan.original_amount,
                        plan.currency,
                      )}
                    </span>
                  )}
                <span className="block font-Poppins font-semibold text-[17px] text-neutral-900 dark:text-neutral-50">
                  {formatPrice(weekly ?? plan.price, plan.currency)}
                </span>
                <span className="block text-[11px] text-neutral-500 dark:text-neutral-400">
                  {t(`${def.copyKey}.${weekly === null ? "perEvent" : "perWeek"}`)}
                </span>
              </span>
            </motion.button>
          );
        })}
      </div>

      {/* What the price does not cover, said next to the price rather than in
          terms nobody opens. */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: stage >= PRICES_STAGE ? 1 : 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="mt-4 text-[11px] leading-relaxed text-neutral-500 dark:text-neutral-400"
      >
        {t(`${def.copyKey}.mealsNotIncluded`)}{" "}
        {t(`${def.copyKey}.autoRenew`)}
      </motion.p>

      {/* The coupon field, folded away behind a text toggle: most people have
          no code, and an open input under the prices invites them to go
          looking for one instead of choosing. */}
      {couponsSupported && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: stage >= PRICES_STAGE ? 1 : 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="mt-3"
        >
          {appliedCoupon ? (
            <div
              className={`flex items-center gap-3 ${ROW_BORDER} ${ROW_SHAPE} ${ROW_SURFACE}`}
            >
              <Tag className="flex-none w-4 h-4 text-secondary-600 dark:text-secondary-400" />
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-semibold text-neutral-900 dark:text-neutral-50">
                  {t("payment.coupon.codeApplied", { code: appliedCoupon })}
                </span>
                <span className="block text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5">
                  {t("payment.coupon.discountReflected")}
                </span>
              </span>
              <button
                type="button"
                onClick={removeCoupon}
                className="flex-none text-[13px] text-neutral-500 dark:text-neutral-400 underline underline-offset-2 hover:text-primary-400 transition-colors"
              >
                {t("payment.coupon.remove")}
              </button>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setCouponOpen((o) => !o)}
                aria-expanded={couponOpen}
                className="flex items-center gap-1.5 text-[13px] text-neutral-500 dark:text-neutral-400 hover:text-primary-400 transition-colors"
              >
                <Tag className="w-3.5 h-3.5" />
                {t("payment.coupon.haveCode")}
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${couponOpen ? "rotate-180" : ""}`}
                />
              </button>
              <StoryBeat open={couponOpen}>
                <div className="flex gap-2 pt-3">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void applyCoupon();
                    }}
                    placeholder={t("payment.coupon.placeholder")}
                    aria-label={t("payment.coupon.placeholder")}
                    className={`flex-1 min-w-0 ${ROW_BORDER} ${ROW_SURFACE} rounded-2xl px-4 py-3 text-[15px] text-neutral-900 dark:text-neutral-50 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  />
                  <button
                    type="button"
                    onClick={() => void applyCoupon()}
                    disabled={!couponCode.trim() || applyingCoupon}
                    className="flex-none rounded-2xl bg-primary-600 px-5 text-[15px] font-semibold text-white disabled:opacity-50 transition-opacity"
                  >
                    {applyingCoupon ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      t("payment.coupon.apply")
                    )}
                  </button>
                </div>
              </StoryBeat>
            </>
          )}
        </motion.div>
      )}

      {/* Risk reversal, under the prices. Above them it answered an objection
          the reader had not had yet; here it lands on the one thing standing
          between someone who wants this and three months of it — that they
          have not tried it. Absent in the guarantee_removal arm. */}
      {guaranteeDays !== null && (
        <StoryBeat open={stage > PRICES_STAGE}>
          {/* The teal is a dark-mode colour: `accent-300` on a near-black panel
              runs 11:1, and the same ink on the light panel ran 1.5:1 — the
              guarantee, the one line here that has to be believed, was the
              palest thing on the screen. Light takes the deep end of the ramp
              instead (title 5.2:1, icon 3.6:1, body 6.9:1), on a fill and edge
              with enough in them to read as a panel rather than a tint. */}
          <div
            className={`mt-4 flex gap-3 ${ROW_BORDER} ${ROW_SHAPE} border-accent-500/40 bg-accent-500/[0.12] dark:border-accent-500/30 dark:bg-accent-500/[0.08]`}
          >
            <motion.span
              className="flex-none mt-0.5"
              initial={{ scale: 0.6 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.25, type: "spring", stiffness: 500, damping: 18 }}
            >
              <ShieldCheck className="w-[18px] h-[18px] text-accent-600 dark:text-accent-400" />
            </motion.span>
            <div>
              <div className="font-semibold text-[14px] text-accent-700 dark:text-accent-300">
                {t(`${def.copyKey}.guarantee.title`)}
              </div>
              <div className="text-[13px] leading-relaxed text-neutral-600 dark:text-neutral-400 mt-1">
                {t(`${def.copyKey}.guarantee.body`, { days: guaranteeDays })}
              </div>
            </div>
          </div>
        </StoryBeat>
      )}

      {/* The quiet way out. Text, never a button. */}
      <motion.button
        type="button"
        initial={{ opacity: 0 }}
        animate={{ opacity: stage > PRICES_STAGE ? 1 : 0 }}
        transition={{ delay: 0.65, duration: 0.4 }}
        onClick={() => {
          tracking.onboardingEscapeToEvents(rt.trackingContext, "offer");
          // Through the save, not straight out — see `OnboardingRuntime.leave`.
          rt.leave("/?from=onboarding");
        }}
        className="mt-auto pt-6 pb-2 self-center text-[13px] text-neutral-500 dark:text-neutral-400 underline underline-offset-2 hover:text-primary-400 transition-colors"
      >
        {t(`${def.copyKey}.browseFirst`)}
      </motion.button>

      {/* The real checkout, over the flow rather than after it. No bookingId:
          nothing is reserved yet, so this buys the membership and the seat is
          claimed with it on the booking page — which is where `finish` lands
          them, on the night they picked, once `onPaid` has seen the
          membership actually exist. */}
      {payingFor && options && (
        <PaymentModal
          bookingId={booking?.id}
          paymentOptions={options}
          preselectedOptionId={payingFor.id}
          couponCode={appliedCoupon ?? undefined}
          startDate={startDate}
          // Without a seat to charge against there is nothing a one-time
          // payment could buy, so the sheet offers memberships only. With one,
          // both are on the table.
          subscriptionOnly={!booking}
          onSuccess={onPaid}
          onCancel={() => setPayingFor(null)}
        />
      )}
    </div>
  );
}
